/**
 * Routen der SPA (codebasiert). Alles außer `/login` und `/forgot` verlangt eine
 * Anmeldung; Mandantenseiten liegen unter `/t/$tenantId`.
 */
import { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { ForgotPasswordPage, LoginPage } from './pages/auth-pages.js';
import { EbayDeclinedPage, EbayReturnPage } from './pages/ebay-pages.js';
import { GalleriesPage, GalleryPage } from './pages/gallery-pages.js';
import { ItemsPage, parseItemsSearch } from './pages/items-page.js';
import { EbayInterfacePage, StartPage } from './pages/start-page.js';
import { FullPageLoader, TenantLayout, TenantRedirect } from './pages/tenant-layout.js';
import type { AuthStore } from './auth/session.js';

export interface RouterContext {
  auth: AuthStore;
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Ausdrücklich überschreiben (wie `parseItemsSearch`): sonst käme z. B. `?redirect=5` roh als Zahl an.
  validateSearch: (raw: Record<string, unknown>): { redirect?: string | undefined } => ({
    redirect: typeof raw['redirect'] === 'string' ? raw['redirect'] : undefined,
  }),
  // Angemeldet: kein zweites Login über die bestehende Sitzung, erst abmelden.
  beforeLoad: async ({ context }) => {
    if (context.auth.getState().status === 'restoring') await context.auth.restore();
    if (context.auth.getState().status === 'signed-in') throw redirect({ to: '/' });
  },
  component: function LoginRoute() {
    const { redirect: target } = loginRoute.useSearch();
    return <LoginPage redirect={target} />;
  },
});

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot',
  component: ForgotPasswordPage,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context, location }) => {
    if (context.auth.getState().status === 'restoring') await context.auth.restore();
    if (context.auth.getState().status !== 'signed-in') {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: Outlet,
});

const indexRoute = createRoute({ getParentRoute: () => authedRoute, path: '/', component: TenantRedirect });

// Rückkehr von eBay (Accept-/Reject-URL des RuName); verlangt eine Anmeldung. Seit 31.10.2026 offen für die Token-Erneuerung
// der App-BETA; außerhalb der BETA antwortet die API weiter 404 (eingefroren), die Seite zeigt dann deren Fehler.
const ebayReturnRoute = createRoute({ getParentRoute: () => authedRoute, path: '/auth/ebay/return', component: EbayReturnPage });
const ebayDeclinedRoute = createRoute({ getParentRoute: () => authedRoute, path: '/auth/ebay/declined', component: EbayDeclinedPage });

const tenantRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/t/$tenantId',
  component: function TenantRoute() {
    const { tenantId } = tenantRoute.useParams();
    return <TenantLayout tenantId={tenantId} />;
  },
});

const startRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/',
  component: function StartRoute() {
    const { tenantId } = tenantRoute.useParams();
    return <StartPage key={tenantId} tenantId={tenantId} />;
  },
});

// Angebote lesend auch in der BETA (Flori 31.10.2026); Aktionen je Angebot schaltet die API über `editable` ab.
const itemsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/items',
  validateSearch: parseItemsSearch,
  component: function ItemsRoute() {
    const { tenantId } = tenantRoute.useParams();
    const search = itemsRoute.useSearch();
    return <ItemsPage key={tenantId} tenantId={tenantId} search={search} />;
  },
});

// eBay-Verbindung mit Live-Update, Zähler und Abgleich (Flori 31.10./01.11.2026, vorher Startseite ohne BETA).
const ebayRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/ebay',
  component: function EbayRoute() {
    const { tenantId } = tenantRoute.useParams();
    return <EbayInterfacePage key={tenantId} tenantId={tenantId} />;
  },
});

const galleriesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/galleries',
  component: function GalleriesRoute() {
    const { tenantId } = tenantRoute.useParams();
    return <GalleriesPage key={tenantId} tenantId={tenantId} />;
  },
});

const galleryRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/galleries/$galleryId',
  component: function GalleryRoute() {
    const { tenantId } = tenantRoute.useParams();
    const { galleryId } = galleryRoute.useParams();
    return <GalleryPage key={`${tenantId}/${galleryId}`} tenantId={tenantId} galleryId={galleryId} />;
  },
});

const articlesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/articles',
  component: function ArticlesRoute() {
    const { tenantId } = tenantRoute.useParams();
    return (
      <Suspense fallback={<FullPageLoader />}>
        <ArticlesPage key={tenantId} tenantId={tenantId} />
      </Suspense>
    );
  },
});

const articleRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/articles/$articleId',
  component: function ArticleRoute() {
    const { tenantId } = tenantRoute.useParams();
    const { articleId } = articleRoute.useParams();
    return (
      <Suspense fallback={<FullPageLoader />}>
        <ArticlePage key={`${tenantId}/${articleId}`} tenantId={tenantId} articleId={articleId} />
      </Suspense>
    );
  },
});

// Artikel- und Designeditor als eigene Bündel (mit dem Renderer `@pat/design-html`): geladen erst beim Öffnen.
const ArticlesPage = lazy(() => import('./pages/article-pages.js').then(m => ({ default: m.ArticlesPage })));
const ArticlePage = lazy(() => import('./pages/article-pages.js').then(m => ({ default: m.ArticlePage })));
const DesignsPage = lazy(() => import('./pages/design-pages.js').then(m => ({ default: m.DesignsPage })));
const DesignPage = lazy(() => import('./pages/design-pages.js').then(m => ({ default: m.DesignPage })));
const MediaPage = lazy(() => import('./pages/media-pages.js').then(m => ({ default: m.MediaPage })));
const AccountPage = lazy(() => import('./pages/account-page.js').then(m => ({ default: m.AccountPage })));

const designsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/designs',
  component: function DesignsRoute() {
    const { tenantId } = tenantRoute.useParams();
    return (
      <Suspense fallback={<FullPageLoader />}>
        <DesignsPage key={tenantId} tenantId={tenantId} />
      </Suspense>
    );
  },
});

const designRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/designs/$designId',
  component: function DesignRoute() {
    const { tenantId } = tenantRoute.useParams();
    const { designId } = designRoute.useParams();
    return (
      <Suspense fallback={<FullPageLoader />}>
        <DesignPage key={`${tenantId}/${designId}`} tenantId={tenantId} designId={designId} />
      </Suspense>
    );
  },
});

const mediaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/media',
  component: function MediaRoute() {
    const { tenantId } = tenantRoute.useParams();
    return (
      <Suspense fallback={<FullPageLoader />}>
        <MediaPage key={tenantId} tenantId={tenantId} />
      </Suspense>
    );
  },
});

const accountRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/account',
  component: function AccountRoute() {
    const { tenantId } = tenantRoute.useParams();
    return (
      <Suspense fallback={<FullPageLoader />}>
        <AccountPage key={tenantId} tenantId={tenantId} />
      </Suspense>
    );
  },
});

// Live-Update steht seit 01.11.2026 (Flori) auf „eBay & Live-Update“; alte Links und Lesezeichen führen dorthin.
const liveUpdateRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: '/live-update',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/t/$tenantId/ebay', params: { tenantId: params.tenantId }, replace: true });
  },
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotRoute,
  authedRoute.addChildren([indexRoute, ebayReturnRoute, ebayDeclinedRoute, tenantRoute.addChildren([startRoute, itemsRoute, ebayRoute, articlesRoute, articleRoute, designsRoute, designRoute, mediaRoute, galleriesRoute, galleryRoute, liveUpdateRoute, accountRoute])]),
]);

export function createAppRouter(context: RouterContext, options: { history?: Parameters<typeof createRouter>[0]['history'] } = {}) {
  return createRouter({ routeTree, context, defaultPreload: false, ...(options.history ? { history: options.history } : {}) });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
